import React, { useEffect, useState } from 'react';
import { AffiliateStats } from '../components/affiliate/AffiliateStats';
import { AffiliateLinkGenerator } from '../components/affiliate/AffiliateLinkGenerator';
import { AffiliateContactForm } from '../components/affiliate/AffiliateContactForm';
import { AffiliatePayouts } from '../components/affiliate/AffiliatePayouts';
import { AffiliatePerformance } from '../components/affiliate/AffiliatePerformance';
import { useAuth } from '../contexts/AuthContext';
import { getAffiliateProfile } from '../services/firebase/affiliateProfile';
import { getUserAffiliateApplication } from '../services/firebase/affiliates';
import { AffiliateProfile } from '../types/affiliate';
import { Button } from '../components/common/Button';
import { useNavigate } from 'react-router-dom';

export const AffiliateDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState<AffiliateProfile | null>(null);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'financials' | 'support'>('overview');

  useEffect(() => {
    const fetchAffiliateProfile = async () => {
      if (!user) return;

      try {
        const profile = await getAffiliateProfile(user.uid);
        if (profile) {
          setAffiliate(profile);
        } else {
          // Check for pending application
          const application = await getUserAffiliateApplication(user.uid);
          if (application) {
            setHasPendingApplication(true);
          } else {
            setError('Affiliate profile not found.');
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load affiliate data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliateProfile();
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (hasPendingApplication || (affiliate && affiliate.status === 'pending')) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Your affiliate application is currently pending approval. You will be notified once it is active.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !affiliate) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-neutral-900 mb-4">Affiliate Program</h2>
        <p className="text-neutral-600 mb-8">{error || "You haven't joined our affiliate program yet."}</p>
        <Button onClick={() => navigate('/affiliateregistration')}>
          Register as an Affiliate
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-neutral-900">Affiliate Dashboard</h1>
        <p className="text-neutral-600">Track your referrals and earnings.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`${
              activeTab === 'performance'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Performance
          </button>
          <button
            onClick={() => setActiveTab('financials')}
            className={`${
              activeTab === 'financials'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Financials
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`${
              activeTab === 'support'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Support
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-8">
        {activeTab === 'overview' && (
          <>
            <AffiliateStats stats={affiliate.stats} />
            <AffiliateLinkGenerator referralCode={affiliate.referralCode} />
          </>
        )}

          {activeTab === 'performance' && (
            <AffiliatePerformance 
              referralCode={affiliate.referralCode} 
              affiliateId={affiliate.userId}
            />
          )}        {activeTab === 'financials' && (
          <AffiliatePayouts affiliate={affiliate} />
        )}

        {activeTab === 'support' && (
          <AffiliateContactForm />
        )}
      </div>
    </div>
  );
};
